// src/components/admin/LeadershipManagement.js
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';

const LeadershipManagement = () => {
  const [leadership, setLeadership] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [currentLeader, setCurrentLeader] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [stats, setStats] = useState({ total: 0, active: 0, unique_roles: 0 });
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [customPosition, setCustomPosition] = useState('');

  const [formData, setFormData] = useState({
    full_name: '',
    role_position: '',
    profile_image: '',
    email: '',
    phone: '',
    linkedin_url: '',
    twitter_url: '',
    facebook_url: '',
    bio: '',
    display_order: 0,
    is_active: true
  });

  useEffect(() => {
    fetchLeadership();
    fetchStats();
  }, [searchTerm, currentPage]);

  const fetchLeadership = async () => {
    try {
      const token = localStorage.getItem('adminToken');

      const API_BASE = process.env.REACT_APP_API_URL;
      if (!API_BASE) {
        throw new Error('REACT_APP_API_URL not configured. Check your .env file.');
      }
      let url = `${API_BASE}/admin/leadership?page=${currentPage}&limit=10`;
      if (searchTerm) url += `&search=${encodeURIComponent(searchTerm)}`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success) {
        setLeadership(data.data || []);
        if (data.pagination) {
          setTotalPages(data.pagination.totalPages);
          setTotalItems(data.pagination.totalItems);
        }
      } else {
        setError(data.message);
        toast.error(data.message || 'Failed to fetch leadership');
      }
    } catch (err) {
      setError('Failed to fetch leadership');
      toast.error('Failed to fetch leadership');
      console.error('Error fetching leadership:', err);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const API_BASE = process.env.REACT_APP_API_URL;
      if (!API_BASE) {
        console.error('REACT_APP_API_URL not configured');
        return;
      }
      const response = await fetch(`${API_BASE}/admin/leadership/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (data.success) {
        setStats(data.data);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const handleCreate = () => {
    setModalMode('create');
    setCurrentLeader(null);
    setImageFile(null);
    setImagePreview(null);
    setSelectedCountry('');
    setCustomPosition('');
    setFormData({
      full_name: '',
      role_position: '',
      profile_image: '',
      email: '',
      phone: '',
      linkedin_url: '',
      twitter_url: '',
      facebook_url: '',
      bio: '',
      display_order: 0,
      is_active: true
    });
    setShowModal(true);
  };

  const handleEdit = (leader) => {
    setModalMode('edit');
    setCurrentLeader(leader);
    setImageFile(null);
    setImagePreview(leader.profile_image || null);

    // Parse country from role_position if it exists (for Country Directors)
    let rolePosition = leader.role_position || '';
    let country = '';
    let customPos = '';

    // Check if it's a predefined position
    const predefinedPositions = [
      'Board Chair', 'Vice-Chair', 'President', 'Vice President',
      'Chief Operating Officer (COO)', 'Chief Financial Officer (CFO)',
      'General Secretary', 'Treasurer', 'Director of Education & Training',
      'Director of Research & Innovation', 'Director of Communications & Assessments',
      'Director of Marketing & Outreach',
      'Director of Membership & Engagement', 'Director of Global Partnerships',
      'Board Member', 'Committee Member', 'Research/Publication Committee',
      'Conference & Events Committee', 'Head of Advisory', 'Advisor',
      'Advisory Board Member', 'Policy Liaison to Ministries of Health',
      'IT', 'Committee Chair', 'Regional Coordinator'
    ];

    // Check for Country Director format
    if (rolePosition.includes(' - ')) {
      const parts = rolePosition.split(' - ');
      rolePosition = parts[0];
      country = parts[1];
    }
    // If it's not a predefined position and not a Country Director, it's a custom "Other" position
    else if (!predefinedPositions.includes(rolePosition) && !rolePosition.startsWith('Country Director') && !rolePosition.startsWith('Vice Country Director')) {
      customPos = rolePosition;
      rolePosition = 'Other';
    }

    setSelectedCountry(country);
    setCustomPosition(customPos);

    setFormData({
      full_name: leader.full_name || '',
      role_position: rolePosition,
      profile_image: leader.profile_image || '',
      email: leader.email || '',
      phone: leader.phone || '',
      linkedin_url: leader.linkedin_url || '',
      twitter_url: leader.twitter_url || '',
      facebook_url: leader.facebook_url || '',
      bio: leader.bio || '',
      display_order: leader.display_order || 0,
      is_active: leader.is_active === 1
    });
    setShowModal(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Only image files (JPEG, PNG, GIF, WebP) are allowed');
        return;
      }

      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image must be less than 5MB');
        return;
      }

      setImageFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadImage = async () => {
    if (!imageFile) return null;

    try {
      setUploadingImage(true);
      const token = localStorage.getItem('adminToken');
      const formData = new FormData();
      formData.append('image', imageFile);

      const API_BASE = process.env.REACT_APP_API_URL;
      if (!API_BASE) {
        throw new Error('API URL not configured');
      }
      const response = await fetch(`${API_BASE}/admin-upload/leadership/image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        return data.data.imagePath;
      } else {
        toast.error(data.message || 'Failed to upload image');
        return null;
      }
    } catch (err) {
      console.error('Error uploading image:', err);
      toast.error('Failed to upload image');
      return null;
    } finally {
      setUploadingImage(false);
    }
  };

  const handleDelete = (id, leaderName) => {
    // Create a custom confirmation toast
    const confirmDelete = () => {
      deleteLeader(id);
      toast.dismiss('delete-confirm');
    };

    const cancelDelete = () => {
      toast.dismiss('delete-confirm');
    };

    toast(
      <div>
        <div className="d-flex align-items-center mb-2">
          <div
            className="rounded-circle d-flex align-items-center justify-content-center me-2"
            style={{
              width: '40px',
              height: '40px',
              backgroundColor: '#6B8E23',
              color: 'white'
            }}
          >
            <i className="bi bi-trash" style={{ fontSize: '18px' }}></i>
          </div>
          <strong style={{ color: '#6B8E23' }}>Delete Leader?</strong>
        </div>
        <p className="mb-3 small text-muted">Are you sure you want to delete "{leaderName}"? This action cannot be undone.</p>
        <div className="d-flex gap-2">
          <button
            className="btn btn-sm btn-danger"
            onClick={confirmDelete}
          >
            Yes, Delete
          </button>
          <button
            className="btn btn-sm btn-secondary"
            onClick={cancelDelete}
          >
            Cancel
          </button>
        </div>
      </div>,
      {
        toastId: 'delete-confirm',
        position: 'top-center',
        autoClose: false,
        closeButton: false,
        hideProgressBar: true,
        closeOnClick: false,
        draggable: false,
        style: {
          borderLeft: '4px solid #6B8E23'
        }
      }
    );
  };

  const deleteLeader = async (id) => {
    try {
      const token = localStorage.getItem('adminToken');
      const API_BASE = process.env.REACT_APP_API_URL;
      if (!API_BASE) {
        throw new Error('API URL not configured');
      }
      const response = await fetch(`${API_BASE}/admin/leadership/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success) {
        fetchLeadership();
        fetchStats();
        toast.success('Leader deleted successfully');
      } else {
        toast.error(data.message || 'Failed to delete leader');
      }
    } catch (err) {
      console.error('Error deleting leader:', err);
      toast.error('Failed to delete leader');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      let imagePath = formData.profile_image;

      // Upload image if a new one was selected
      if (imageFile) {
        imagePath = await uploadImage();
        if (!imagePath) {
          return; // Upload failed
        }
      }

      const token = localStorage.getItem('adminToken');
      const API_BASE = process.env.REACT_APP_API_URL;
      if (!API_BASE) {
        throw new Error('API URL not configured');
      }
      const url = modalMode === 'create'
        ? `${API_BASE}/admin/leadership`
        : `${API_BASE}/admin/leadership/${currentLeader.id}`;

      const method = modalMode === 'create' ? 'POST' : 'PUT';

      // Construct role_position with country for Country Directors or custom position for "Other"
      let finalRolePosition = formData.role_position;
      if ((formData.role_position === 'Country Director' || formData.role_position === 'Vice Country Director') && selectedCountry) {
        finalRolePosition = `${formData.role_position} - ${selectedCountry}`;
      } else if (formData.role_position === 'Other' && customPosition) {
        finalRolePosition = customPosition;
      }

      const submitData = {
        ...formData,
        role_position: finalRolePosition,
        profile_image: imagePath
      };

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submitData)
      });

      const data = await response.json();

      if (data.success) {
        setShowModal(false);
        fetchLeadership();
        fetchStats();
        toast.success(`Leader ${modalMode === 'create' ? 'created' : 'updated'} successfully`);
      } else {
        toast.error(data.message || `Failed to ${modalMode === 'create' ? 'create' : 'update'} leader`);
      }
    } catch (err) {
      console.error(`Error ${modalMode === 'create' ? 'creating' : 'updating'} leader:`, err);
      toast.error(`Failed to ${modalMode === 'create' ? 'create' : 'update'} leader`);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  if (loading && leadership.length === 0) {
    return (
      <div className="container mt-4">
        <div className="text-center">
          <div className="spinner-border" style={{ color: '#6B8E23' }} role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0" style={{ color: '#6B8E23' }}>Leadership Management</h2>
        <button
          className="btn btn-sm"
          style={{ backgroundColor: '#6B8E23', color: 'white' }}
          onClick={handleCreate}
        >
          <i className="bi bi-plus-lg me-2"></i>Add New Leader
        </button>
      </div>

      {/* Statistics Cards */}
      <div className="row mb-4">
        <div className="col-md-4">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <h6 className="text-muted mb-2">Total Leaders</h6>
              <h3 className="mb-0" style={{ color: '#6B8E23' }}>{stats.total || 0}</h3>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <h6 className="text-muted mb-2">Active Leaders</h6>
              <h3 className="mb-0" style={{ color: '#6B8E23' }}>{stats.active || 0}</h3>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <h6 className="text-muted mb-2">Unique Positions</h6>
              <h3 className="mb-0" style={{ color: '#6B8E23' }}>{stats.unique_roles || 0}</h3>
            </div>
          </div>
        </div>
      </div>

      {/* Category Tabs */}
      <ul className="nav nav-tabs mb-4 d-flex flex-row flex-nowrap" style={{ borderBottom: '2px solid #dee2e6', overflowX: 'auto' }}>
        {['all', 'executive', 'country_directors', 'vice_directors', 'board', 'advisory', 'other'].map(category => {
          const labels = {
            all: 'All Leaders',
            executive: 'Executive Board',
            country_directors: 'Country Directors',
            vice_directors: 'Vice Country Directors',
            board: 'Board Members',
            advisory: 'Advisory Board',
            other: 'Other Positions'
          };
          return (
            <li className="nav-item" key={category}>
              <button
                className={`nav-link fw-bold text-nowrap ${activeCategory === category ? 'text-white' : 'text-dark'}`}
                onClick={() => setActiveCategory(category)}
                style={{
                  backgroundColor: activeCategory === category ? '#6B8E23' : 'transparent',
                  border: 'none',
                  borderBottom: activeCategory === category ? '3px solid #6B8E23' : '3px solid transparent',
                  padding: '12px 24px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
              >
                {labels[category]}
              </button>
            </li>
          );
        })}
      </ul>

      {/* Search */}
      <div className="card mb-4 border-0 shadow-sm">
        <div className="card-body">
          <div className="row">
            <div className="col-md-6">
              <input
                type="text"
                className="form-control"
                placeholder="Search by name, email, or position..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Leadership Table */}
      <div className="card border-0 shadow-sm">
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>Image</th>
                  <th>Name</th>
                  <th>Position</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Status</th>
                  <th>Order</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {leadership
                  .filter(leader => {
                    if (activeCategory === 'all') return true;
                    const position = leader.role_position.toLowerCase();
                    if (activeCategory === 'executive') return ['president', 'secretary general', 'treasurer'].some(p => position.includes(p));
                    if (activeCategory === 'country_directors') return position.includes('country director') && !position.includes('vice');
                    if (activeCategory === 'vice_directors') return position.includes('vice country director');
                    if (activeCategory === 'board') return position.includes('board member') && !position.includes('advisory');
                    if (activeCategory === 'advisory') return position.includes('advisory');
                    if (activeCategory === 'other') return !['president', 'secretary', 'treasurer', 'country director', 'board member', 'advisory'].some(p => position.includes(p));
                    return true;
                  })
                  .sort((a, b) => {
                    // Sort by display_order (ascending)
                    const orderA = parseInt(a.display_order) || 0;
                    const orderB = parseInt(b.display_order) || 0;
                    return orderA - orderB;
                  })
                  .map((leader) => {
                    // Get image source - handle uploaded images
                    const getImageSrc = () => {
                      if (!leader.profile_image) return null;

                      const API_BASE = process.env.REACT_APP_API_URL;
                      if (!API_BASE) return null;

                      // If it's an uploaded image
                      if (leader.profile_image.startsWith('/uploads/')) {
                        const baseUrl = `${API_BASE}${leader.profile_image}`;
                        // Add cache-busting to force reload of updated images
                        return `${baseUrl}?v=${Date.now()}`;
                      }

                      // For asset images, construct full URL (admin can't use require)
                      if (leader.profile_image.startsWith('/assets/')) {
                        // Assets are served from frontend, use window.location for full path
                        return `${window.location.origin}${leader.profile_image}`;
                      }

                      // Direct URL
                      return leader.profile_image;
                    };

                    const imageSrc = getImageSrc();

                    return (
                      <tr key={leader.id}>
                        <td>
                          {imageSrc ? (
                            <img
                              src={imageSrc}
                              alt={leader.full_name}
                              style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '50%' }}
                            />
                          ) : (
                            <div
                              style={{
                                width: '50px',
                                height: '50px',
                                borderRadius: '50%',
                                backgroundColor: '#6B8E23',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontWeight: 'bold'
                              }}
                            >
                              {leader.full_name.charAt(0)}
                            </div>
                          )}
                        </td>
                        <td>{leader.full_name}</td>
                        <td>{leader.role_position}</td>
                        <td>{leader.email || '-'}</td>
                        <td>{leader.phone || '-'}</td>
                        <td>
                          <span className={`badge ${leader.is_active ? 'bg-success' : 'bg-secondary'}`}>
                            {leader.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td>{leader.display_order}</td>
                        <td>
                          <button
                            className="btn btn-sm btn-outline-primary me-2"
                            onClick={() => handleEdit(leader)}
                            title="Edit"
                          >
                            ✏️ Edit
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDelete(leader.id, leader.full_name)}
                            title="Delete"
                          >
                            🗑️ Delete
                          </button>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <nav className="mt-4">
              <ul className="pagination justify-content-center">
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>
                </li>
                {[...Array(totalPages)].map((_, index) => (
                  <li key={index + 1} className={`page-item ${currentPage === index + 1 ? 'active' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(index + 1)}
                      style={currentPage === index + 1 ? { backgroundColor: '#6B8E23', borderColor: '#6B8E23' } : {}}
                    >
                      {index + 1}
                    </button>
                  </li>
                ))}
                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </li>
              </ul>
              <p className="text-center text-muted">
                Showing page {currentPage} of {totalPages} ({totalItems} total items)
              </p>
            </nav>
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header" style={{ backgroundColor: '#6B8E23', color: 'white' }}>
                <h5 className="modal-title">
                  {modalMode === 'create' ? 'Add New Leader' : 'Edit Leader'}
                </h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowModal(false)}></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Full Name *</label>
                      <input
                        type="text"
                        className="form-control"
                        name="full_name"
                        value={formData.full_name}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Leadership Position *</label>
                      <select
                        className="form-select"
                        name="role_position"
                        value={formData.role_position}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select Position</option>
                        <optgroup label="Board Leadership">
                          <option value="Board Chair">Board Chair</option>
                          <option value="Vice-Chair">Vice-Chair</option>
                        </optgroup>
                        <optgroup label="Executive Officers">
                          <option value="President">President</option>
                          <option value="Vice President">Vice President</option>
                          <option value="Chief Operating Officer (COO)">Chief Operating Officer (COO)</option>
                          <option value="Chief Financial Officer (CFO)">Chief Financial Officer (CFO)</option>
                          <option value="General Secretary">General Secretary</option>
                          <option value="Treasurer">Treasurer</option>
                        </optgroup>
                        <optgroup label="Directors">
                          <option value="Director of Education & Training">Director of Education & Training</option>
                          <option value="Director of Research & Innovation">Director of Research & Innovation</option>
                          <option value="Director of Communications & Assessments">Director of Communications & Assessments</option>
                          <option value="Director of Marketing & Outreach">Director of Marketing & Outreach</option>
                          <option value="Director of Membership & Engagement">Director of Membership & Engagement</option>
                          <option value="Director of Global Partnerships">Director of Global Partnerships</option>
                        </optgroup>
                        <optgroup label="Country Directors">
                          <option value="Country Director">Country Director</option>
                          <option value="Vice Country Director">Vice Country Director</option>
                        </optgroup>
                        <optgroup label="Board & Committees">
                          <option value="Board Member">Board Member</option>
                          <option value="Committee Member">Committee Member</option>
                          <option value="Research/Publication Committee">Research/Publication Committee</option>
                          <option value="Conference & Events Committee">Conference & Events Committee</option>
                        </optgroup>
                        <optgroup label="Advisory & Policy">
                          <option value="Head of Advisory">Head of Advisory</option>
                          <option value="Advisor">Advisor</option>
                          <option value="Advisory Board Member">Advisory Board Member</option>
                          <option value="Policy Liaison to Ministries of Health">Policy Liaison to Ministries of Health</option>
                        </optgroup>
                        <optgroup label="Other Positions">
                          <option value="IT">IT</option>
                          <option value="Committee Chair">Committee Chair</option>
                          <option value="Regional Coordinator">Regional Coordinator</option>
                          <option value="Other">Other (Specify Below)</option>
                        </optgroup>
                      </select>
                    </div>
                  </div>

                  {/* Country Selection for Country Directors */}
                  {(formData.role_position === 'Country Director' || formData.role_position === 'Vice Country Director') && (
                    <div className="mb-3">
                      <label className="form-label">Country *</label>
                      <input
                        type="text"
                        className="form-control"
                        name="country"
                        value={selectedCountry}
                        onChange={(e) => setSelectedCountry(e.target.value)}
                        placeholder="e.g., Ghana, Nigeria, Kenya, etc."
                        required
                      />
                      <small className="text-muted">This will be added to your position title (e.g., "Country Director - Ghana")</small>
                    </div>
                  )}

                  {/* Custom Position Input for "Other" */}
                  {formData.role_position === 'Other' && (
                    <div className="mb-3">
                      <label className="form-label">Specify Position *</label>
                      <input
                        type="text"
                        className="form-control"
                        name="custom_position"
                        value={customPosition}
                        onChange={(e) => setCustomPosition(e.target.value)}
                        placeholder="e.g., Chief Technology Officer, Media Coordinator, etc."
                        required
                      />
                      <small className="text-muted">Enter the custom position title</small>
                    </div>
                  )}

                  <div className="mb-3">
                    <label className="form-label">Profile Image</label>
                    <input
                      type="file"
                      className="form-control"
                      accept="image/*"
                      onChange={handleImageChange}
                    />
                    {imagePreview && (
                      <div className="mt-2">
                        <img
                          src={imagePreview.startsWith('data:') ? imagePreview : `${process.env.REACT_APP_API_URL}${imagePreview}`}
                          alt="Preview"
                          style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'cover', borderRadius: '8px' }}
                          onError={(e) => {
                            console.error('Image failed to load:', imagePreview);
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                    )}
                  </div>

                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Email</label>
                      <input
                        type="email"
                        className="form-control"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Phone</label>
                      <input
                        type="text"
                        className="form-control"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Bio</label>
                    <textarea
                      className="form-control"
                      name="bio"
                      rows="3"
                      value={formData.bio}
                      onChange={handleInputChange}
                    ></textarea>
                  </div>

                  <div className="row">
                    <div className="col-md-4 mb-3">
                      <label className="form-label">LinkedIn URL</label>
                      <input
                        type="url"
                        className="form-control"
                        name="linkedin_url"
                        value={formData.linkedin_url}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="col-md-4 mb-3">
                      <label className="form-label">Twitter URL</label>
                      <input
                        type="url"
                        className="form-control"
                        name="twitter_url"
                        value={formData.twitter_url}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="col-md-4 mb-3">
                      <label className="form-label">Facebook URL</label>
                      <input
                        type="url"
                        className="form-control"
                        name="facebook_url"
                        value={formData.facebook_url}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Display Order</label>
                      <input
                        type="number"
                        className="form-control"
                        name="display_order"
                        value={formData.display_order}
                        onChange={handleInputChange}
                      />
                      <small className="text-muted">Lower numbers appear first. If this position is taken, existing leaders will automatically shift down.</small>
                    </div>
                    <div className="col-md-6 mb-3">
                      <div className="form-check mt-4">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="is_active"
                          name="is_active"
                          checked={formData.is_active}
                          onChange={handleInputChange}
                        />
                        <label className="form-check-label" htmlFor="is_active">
                          Active
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn"
                    style={{ backgroundColor: '#6B8E23', color: 'white' }}
                    disabled={uploadingImage}
                  >
                    {uploadingImage ? 'Uploading...' : (modalMode === 'create' ? 'Create Leader' : 'Update Leader')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadershipManagement;
